import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { Leaf, ArrowRight, Sparkles, Clock } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface FeaturedProduct {
  id: string;
  product_id: string;
  display_order: number;
  custom_image_url: string | null;
  custom_title: string | null;
  custom_description: string | null;
  badge_text: string | null;
  is_active: boolean;
  section_id: string | null;
  products: {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    image_url: string | null;
    categories: {
      name: string;
      slug: string;
    } | null;
  } | null;
}

interface FeaturedSection {
  id: string;
  name: string;
  description: string | null;
  display_order: number;
  is_active: boolean;
  featured_products: FeaturedProduct[];
}

const FeaturedProducts = () => {
  // Fetch sections with their featured products
  const { data: sections, isLoading } = useQuery({
    queryKey: ["featured-products-by-section"],
    queryFn: async () => {
      // First fetch sections
      const { data: sectionsData, error: sectionsError } = await supabase
        .from("featured_sections")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (sectionsError) throw sectionsError;

      // Then fetch all featured products
      const { data: productsData, error: productsError } = await supabase
        .from("featured_products")
        .select(`
          *,
          products (
            id, name, slug, description, image_url,
            categories (name, slug)
          )
        `)
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (productsError) throw productsError;

      // Group products by section
      // Keep items where: product exists OR badge is "Próximamente" (coming soon products with custom image)
      const validProducts = (productsData as FeaturedProduct[]).filter(p => 
        p.products !== null || (p.badge_text === "Próximamente" && p.custom_image_url)
      );
      const result: FeaturedSection[] = [];
      
      // Add products without section first (as "General" or ungrouped)
      const unsectionedProducts = validProducts.filter(p => !p.section_id);
      if (unsectionedProducts.length > 0) {
        result.push({
          id: "unsectioned",
          name: "",
          description: null,
          display_order: -1,
          is_active: true,
          featured_products: unsectionedProducts,
        });
      }

      // Add sectioned products
      for (const section of sectionsData || []) {
        const sectionProducts = validProducts.filter(
          p => p.section_id === section.id
        );
        if (sectionProducts.length > 0) {
          result.push({
            ...section,
            featured_products: sectionProducts,
          });
        }
      }

      return result;
    },
  });

  // Check if we have any products
  const hasProducts = sections && sections.some(s => s.featured_products.length > 0);

  // Don't render section if no featured products
  if (!isLoading && !hasProducts) {
    return null;
  }

  return (
    <section className="py-16 sm:py-24 bg-gradient-to-b from-background to-accent/20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-accent/80 border border-primary/15 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-primary text-sm font-semibold">Destacados</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
            Productos <span className="text-primary">Destacados</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Descubre nuestras soluciones más populares y efectivas para el control de plagas
          </p>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-4">
                <Skeleton className="aspect-[4/5] rounded-2xl" />
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-full" />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-16">
            {sections?.map((section) => (
              <div key={section.id}>
                {/* Section Title (only show if section has a name) */}
                {section.name && (
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-foreground flex items-center gap-3">
                      <span className="h-px flex-1 max-w-12 bg-primary/30" />
                      {section.name}
                      <span className="h-px flex-1 max-w-12 bg-primary/30" />
                    </h3>
                    {section.description && (
                      <p className="text-muted-foreground text-center mt-2">
                        {section.description}
                      </p>
                    )}
                  </div>
                )}

                {/* Products Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {section.featured_products.map((item, index) => (
                    <ProductCard key={item.id} item={item} index={index} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* View All Products Link */}
        <div className="text-center mt-12">
          <Link
            to="/productos"
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-semibold transition-colors group"
          >
            Ver todos los productos
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};

function ProductCard({ item, index }: { item: FeaturedProduct; index: number }) {
  const product = item.products;
  const badge = item.badge_text;
  const isComingSoon = badge === "Próximamente";
  
  // Safety check - don't render if product is null AND it's not a "Próximamente" item with custom data
  if (!product && !(isComingSoon && item.custom_image_url)) return null;
  
  const imageUrl = item.custom_image_url || product?.image_url;
  const title = item.custom_title || product?.name || "Próximamente";
  const description = item.custom_description || product?.description;

  const getBadgeStyles = () => {
    if (isComingSoon) {
      return "bg-muted text-muted-foreground";
    }
    return "bg-primary text-primary-foreground";
  };

  const getBadgeIcon = () => {
    if (isComingSoon) {
      return <Clock className="w-3 h-3" />;
    }
    return <Leaf className="w-3 h-3" />;
  };

  // For "Próximamente" items without a product, don't link anywhere
  const CardWrapper = ({ children }: { children: React.ReactNode }) => {
    if (isComingSoon && !product) {
      return (
        <div
          className="group relative block cursor-default"
          style={{ animationDelay: `${index * 100}ms` }}
        >
          {children}
        </div>
      );
    }
    return (
      <Link
        to={`/productos/${product?.slug || ""}`}
        className="group relative block"
        style={{ animationDelay: `${index * 100}ms` }}
      >
        {children}
      </Link>
    );
  };

  return (
    <CardWrapper>
      <div className="relative overflow-hidden rounded-2xl bg-card border border-border/50 shadow-nature hover:shadow-hover transition-all duration-500 group-hover:-translate-y-2">
        {/* Badge */}
        {badge && (
          <div className="absolute top-4 left-4 z-10">
            <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full shadow-lg ${getBadgeStyles()}`}>
              {getBadgeIcon()}
              {badge}
            </span>
          </div>
        )}

        {/* Image Container */}
        <div className="aspect-square relative overflow-hidden bg-accent/30">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={title}
              className="w-full h-full object-contain p-4 transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Leaf className="w-16 h-16 text-primary/30" />
            </div>
          )}
        </div>

        {/* Content - Below Image */}
        <div className="p-5 bg-card">
          {product?.categories && (
            <span className="text-xs text-primary font-medium uppercase tracking-wider mb-2 block">
              {product.categories.name}
            </span>
          )}
          <h3 className="text-base font-bold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors leading-tight">
            {title}
          </h3>
          {description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-4 leading-relaxed">
              {description}
            </p>
          )}
          {isComingSoon && !product ? (
            <span className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground">
              <Clock className="w-4 h-4" />
              Disponible pronto
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary group-hover:gap-2 transition-all">
              Ver Producto
              <ArrowRight className="w-4 h-4" />
            </span>
          )}
        </div>
      </div>
    </CardWrapper>
  );
}

export default FeaturedProducts;
